import { Button, Fade, Stack, styled, TextField, Typography } from '@mui/material';
import { useClickAway } from '@uidotdev/usehooks';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { DynamicIcon, Tooltip } from 'src/components';
import { validateEmail } from 'src/components/Forms/validations';
import { SignalOnCallScheduleManagementMenu } from './signal-oncall-schedule-menu';
// import { DzOncallManagementMenu } from './dz-oncall-management-agents-list/dz-oncall-management-menu';

const StyledForm = styled('form')({
  display: 'flex',
  flexDirection: 'row',
  gap: '0.5rem',
  alignItems: 'flex-end',
});

export interface SignalOnCallScheduleNotificationEmailFormProps {
  email?: string | null;
  partnerId: number;
  onSubmit: (email?: string) => Promise<void>;
  disabled: boolean;
}
export interface NotificationEmailFormData {
  email: string;
}

enum SignalNotificationEmailFormFields {
  Email = 'email',
}

type SignalNotificationEmailFormValues = {
  [SignalNotificationEmailFormFields.Email]: string;
};

const defaultFormValues: SignalNotificationEmailFormValues = {
  [SignalNotificationEmailFormFields.Email]: '',
};

const GLOBAL_NOTIFICATIONS_TOOLTIP_TEXT =
  'Send all Signal notifications to a shared email address, like a team inbox. Signal will still notify who is on call.';

export const SignalOnCallScheduleNotificationEmailForm = ({
  email,
  partnerId,
  onSubmit,
  disabled,
}: SignalOnCallScheduleNotificationEmailFormProps) => {
  const [isEditing, setIsEditing] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { isSubmitting, isDirty, isValid, errors, isSubmitted },
  } = useForm({
    defaultValues: defaultFormValues,
  });

  const ref = useClickAway<HTMLDivElement>(() => {
    setIsEditing(false);
    reset();
  });

  const submitForm = useCallback(
    async (data: SignalNotificationEmailFormValues) => {
      const notificationEmail = data.email;
      if (!email && !notificationEmail) {
        setIsEditing(false);
        return;
      }

      await onSubmit(notificationEmail);
      setIsEditing(false);
    },
    [email, onSubmit],
  );

  useEffect(() => {
    reset({
      [SignalNotificationEmailFormFields.Email]: email || '',
    });
  }, [email, reset]);

  return (
    <Stack p="1rem" pt={0} width="20rem" overflow="hidden" flexShrink={0} gap={1} ref={ref}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Typography variant="subtitle2">Global Notifications</Typography>
          <Tooltip title={GLOBAL_NOTIFICATIONS_TOOLTIP_TEXT}>
            <DynamicIcon
              aria-label="Global Notifications help"
              component="span"
              fontSize="1rem"
              name="info"
              sx={{ color: 'text.secondary', cursor: 'help' }}
              tabIndex={0}
            />
          </Tooltip>
        </Stack>
        <SignalOnCallScheduleManagementMenu partnerId={partnerId} />
      </Stack>
      {!isEditing ? (
        <Tooltip
          title={
            <>
              <Typography variant="body2">{email}</Typography>
              {!disabled && (
                <Typography variant="body2">Click to change notification email.</Typography>
              )}
            </>
          }
        >
          <Typography
            sx={{ cursor: 'pointer', lineHeight: '2rem' }}
            variant="subtitle2"
            onClick={() => setIsEditing(true)}
          >
            {email || '+ Add email'}
          </Typography>
        </Tooltip>
      ) : (
        <StyledForm onSubmit={handleSubmit(submitForm)}>
          <TextField
            {...register(SignalNotificationEmailFormFields.Email, {
              validate: (value) =>
                !value || (value && validateEmail(value)) || 'Invalid email address',
            })}
            disabled={disabled || isSubmitting}
            type="email"
            error={!!errors[SignalNotificationEmailFormFields.Email]}
            placeholder="+ Add email"
            fullWidth
            variant="standard"
            InputProps={{ sx: { fontSize: '1rem' } }}
          />
          <Fade in={isEditing}>
            <Button
              disabled={disabled || isSubmitting || !isDirty || (isSubmitted && !isValid)}
              disableRipple
              variant="text"
              sx={{ mt: '0.5rem' }}
              type="submit"
            >
              Save
            </Button>
          </Fade>
        </StyledForm>
      )}
    </Stack>
  );
};
